import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pipefitterPipeBasedGenerateSequence } from '../../../../redux/Pipefitter/actions';
import {
  GenerateSequenceModal,
  type GenerateSequenceFormValues,
  type PipeBasedGenerateSubmitValues,
  type PipeSelectionItem,
  type PipeSpecificationForSequence,
} from '../GenerateSequenceModal';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { Input } from '../../../../components/ui/input';
import type { DashboardProject } from '../../../project/types/project';
import type { CompatReportDocument } from './types';
import {
  bandLabel,
  formatPct,
  resolvePerPipeDisplayRows,
} from './helpers/formatReport';
import type { SetupPipeDefaults } from './views/SetupView';

interface GenerateSequenceFromCompatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  result: CompatReportDocument;
  selectedPipe?: SetupPipeDefaults | null;
}

function pipeIdsMatchingFilter(
  items: Array<{ pipeId: string; goesInRate: number }>,
  filterEnabled: boolean,
  belowPct: number
): string[] {
  if (!filterEnabled) return items.map((p) => p.pipeId);
  const threshold = Number.isFinite(belowPct) ? Math.min(100, Math.max(0, belowPct)) : 100;
  // 100% → all pipes; otherwise include rates up to and including the threshold (e.g. 95% includes 95%).
  if (threshold >= 100) return items.map((p) => p.pipeId);
  return items
    .filter((p) => p.goesInRate * 100 <= threshold)
    .map((p) => p.pipeId);
}

function buildInitialValues(
  selectedPipe: SetupPipeDefaults | null | undefined,
  result: CompatReportDocument,
  selectedCount: number
): Partial<GenerateSequenceFormValues> {
  const count = Math.max(selectedCount, 1);
  const lenRaw = Number(
    (selectedPipe as any)?.nominalPipeLengthFt ??
      (selectedPipe as any)?.nominalPipeLength ??
      40
  );
  const len = Number.isFinite(lenRaw) && lenRaw > 0 ? lenRaw : 40;
  const odRaw = Number(
    selectedPipe?.nominalOd ??
      selectedPipe?.nominalOdMax ??
      selectedPipe?.nominalOdMin ??
      result.settings?.nominalODmm ??
      508
  );
  const od = Number.isFinite(odRaw) && odRaw > 0 ? odRaw : 508;
  const s = result.settings;
  const rotationRaw = String(s?.rotation ?? '').toLowerCase();
  const pipeRotation: GenerateSequenceFormValues['pipeRotation'] | undefined =
    rotationRaw.includes('anti') || rotationRaw === 'ccw'
      ? 'antiClockwise'
      : rotationRaw.includes('clock') || rotationRaw === 'cw'
        ? 'clockwise'
        : undefined;

  const round2 = (n: number) => Math.round(n * 100) / 100;

  return {
    nominalPipeLengthFt: len,
    targetNumberOfPipes: count,
    targetPipelineLengthFt: len * count,
    nominalODMm: od,
    ...(s?.hiLoThresholdMm != null ? { hiloThresholdMm: round2(s.hiLoThresholdMm) } : {}),
    ...(s?.sixOClockExclusionDeg != null
      ? { exclusion6OClockMm: round2(s.sixOClockExclusionDeg) }
      : {}),
    ...(s?.weldSeamExclusionDeg != null
      ? { weldSeamExclusionMm: round2(s.weldSeamExclusionDeg) }
      : {}),
    ...(pipeRotation ? { pipeRotation } : {}),
  };
}

/**
 * Generate Sequence from a finished random-compat report.
 * Reuses GenerateSequenceModal + the same pipe-based generate API; does not change the existing Sequence tab flow.
 */
export const GenerateSequenceFromCompatModal: React.FC<GenerateSequenceFromCompatModalProps> = ({
  open,
  onOpenChange,
  project,
  result,
  selectedPipe,
}) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber ?? result.project?.number ?? '';
  const isSequenceLoading = useSelector(
    (state: any) => state.pipefitterSequence?.isSequenceLoading ?? false
  );
  const configurationList = useSelector(
    (state: any) => state.pipefitterConfiguration?.configurationList || []
  );

  const perPipeRows = useMemo(
    () => resolvePerPipeDisplayRows(result.weldability).filter((p) => !p.isDeadEnd),
    [result.weldability]
  );

  const pipeSelectionItems: PipeSelectionItem[] = useMemo(
    () =>
      perPipeRows.map((p) => ({
        id: p.pipeId,
        label: p.pipeId,
        status: `${formatPct(p.goesInRate)} · ${bandLabel(p.band)}`,
        subLabel: formatPct(p.goesInRate),
      })),
    [perPipeRows]
  );

  const [filterEnabled, setFilterEnabled] = useState(false);
  const [belowPct, setBelowPct] = useState(95);
  const [selectedPipeIds, setSelectedPipeIds] = useState<string[]>([]);

  // When modal opens or filter changes, re-select matching pipes (user can still toggle).
  useEffect(() => {
    if (!open) return;
    setSelectedPipeIds(pipeIdsMatchingFilter(perPipeRows, filterEnabled, belowPct));
  }, [open, filterEnabled, belowPct, perPipeRows]);

  useEffect(() => {
    if (!open) {
      setFilterEnabled(false);
      setBelowPct(95);
    }
  }, [open]);

  const pipeSpecification: PipeSpecificationForSequence | null = useMemo(() => {
    const values = buildInitialValues(selectedPipe, result, selectedPipeIds.length);
    return {
      nominalPipeLengthFt: values.nominalPipeLengthFt ?? 40,
      nominalODMm: values.nominalODMm ?? 508,
    };
  }, [selectedPipe, result, selectedPipeIds.length]);

  const initialValues = useMemo(
    () => buildInitialValues(selectedPipe, result, selectedPipeIds.length),
    [selectedPipe, result, selectedPipeIds.length]
  );

  const sequences = useMemo(() => {
    const options: Array<{ label: string; value: string; sequenceName: string }> = [];
    (configurationList || []).forEach((spread: any) => {
      options.push({
        label: `Sequence: ${spread.spreadName}`,
        value: `spread:${spread.id}`,
        sequenceName: spread.spreadName ?? '',
      });
      (spread.subSpreads || []).forEach((sub: { id: number; subSpreadName?: string }) => {
        options.push({
          label: `Sequence: ${spread.spreadName} > ${sub.subSpreadName ?? 'Sub-sequence'}`,
          value: `subSpread:${sub.id}`,
          sequenceName: sub.subSpreadName ?? spread.spreadName ?? '',
        });
      });
    });
    return options;
  }, [configurationList]);

  const handleSubmit = (values: PipeBasedGenerateSubmitValues) => {
    if (!projectNumber || selectedPipeIds.length === 0) return;
    const mode =
      values.mode === 'new' ? 'NEW' : values.mode === 'regenerate' ? 'REGENERATE' : 'APPEND';
    const payload = {
      projectNumber,
      mode,
      sequenceName: values.sequenceName,
      pipeIds: selectedPipeIds,
      ...(values.existingSequenceRef && Object.keys(values.existingSequenceRef).length > 0
        ? { existingSequenceRef: values.existingSequenceRef }
        : {}),
      config: values.config,
      ...(values.notifyEmail ? { notifyEmail: values.notifyEmail } : {}),
      ...(values.notifyEmails && values.notifyEmails.length > 0
        ? { notifyEmails: values.notifyEmails }
        : {}),
    };
    dispatch(
      pipefitterPipeBasedGenerateSequence(
        payload,
        () => {
          onOpenChange(false);
        },
        () => {}
      )
    );
  };

  const pipeSelectionToolbar = (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
        <div className="min-w-0">
          <Label htmlFor="compat-pipe-filter-switch" className="text-sm font-medium text-gray-900">
            Select pipes
          </Label>
          <p className="text-xs text-gray-500 mt-0.5">
            {filterEnabled
              ? `${belowPct}% (${selectedPipeIds.length} of ${perPipeRows.length})`
              : 'All pipes selected'}
          </p>
        </div>
        <Switch
          id="compat-pipe-filter-switch"
          checked={filterEnabled}
          onCheckedChange={setFilterEnabled}
          className="data-[state=unchecked]:bg-galvanizing-green data-[state=checked]:bg-[#EB1C37]"
        />
      </div>
      {filterEnabled && (
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm font-medium text-gray-700">Filter by %</Label>
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={belowPct}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setBelowPct(0);
                    return;
                  }
                  const n = Number(raw);
                  if (Number.isNaN(n)) return;
                  // Allow one decimal place (e.g. 95.1)
                  const rounded = Math.round(n * 10) / 10;
                  setBelowPct(Math.min(100, Math.max(0, rounded)));
                }}
                className="h-8 w-24 px-2 text-sm font-semibold text-gray-900 tabular-nums text-center"
                aria-label="Filter percentage value"
              />
              <span className="text-sm font-semibold text-hero-red">%</span>
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={belowPct}
            onChange={(e) => setBelowPct(Number(e.target.value))}
            className="w-full accent-hero-red"
            aria-label="Filter by percentage"
          />
          <div className="flex justify-between text-[11px] text-gray-400">
            <span>0%</span>
            <span>100% = all pipes</span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <GenerateSequenceModal
      open={open}
      onOpenChange={onOpenChange}
      title="Generate Sequence from compatibility"
      submitLabel="Generate"
      sequenceModeOptions="none"
      modeOverride="new"
      lockModeSelection
      isSubmitting={isSequenceLoading}
      initialValues={initialValues}
      selectedPipeCount={selectedPipeIds.length}
      pipeIds={selectedPipeIds}
      sequences={sequences}
      pipeSpecification={pipeSpecification}
      syncMetricsFromPipeSelection
      applyInitialValuesWhileOpen
      pipeSelectionItems={pipeSelectionItems}
      selectedPipeIds={selectedPipeIds}
      onSelectedPipeIdsChange={setSelectedPipeIds}
      simplePipeSelectionTable={false}
      pipeSelectionToolbar={pipeSelectionToolbar}
      onSubmit={handleSubmit}
    />
  );
};
